import { createLogger } from 'shared/services/logger';
import { DRAW_MODE_NAMES, WV_LAYER_NAMES } from 'shared/stores/annotations/consts';
import { store } from 'shared/stores/core/decorators';
import { pull, pushOnce } from 'shared/util';
import { DRAW_MODES } from './consts';
import { translateRegion } from './legacy-translations';
import {translate} from 'shared/services/I18nService';
const $t = translate.scope('uScope.extensions.annotations');

const logger = createLogger('@store(DRAW_MODE)');

const DRAW_MODE_LOCK = {lockOwner: 'annotations.ui-title-wait', modes: DRAW_MODE_NAMES};
const DEFAULT_OWNER = 'annotations.ui-title';

// Defines actions that can be performed to create annotations (e.g. layers and regions). Currently, these actions just
// wrap methods on the original annotations service, listen for events on it, and update state accordingly.
@store('DRAW_MODE')
export class DrawingStore {
  state () {
    const state = {
      drawModes: [],
      drawMode: null,
      isDrawing: false,

      // "locks" is a list of services that have requested control of the annotations tools. Locks are stored keys who's
      // name serves two purposes:
      // 1. To identify an "owner". An owner is whoever has control over layer selection. This allows externals such as
      //    ImageAnalysis to take control over which layer should be used for drawing.
      // 2. To specify an i18nKey. This is used to update the title bar of the tools dialog with the name of the owner.
      //    In this way users can see what "mode" the drawing tools are in.
      locks: [{displayKey: DEFAULT_OWNER, lockOwner: DEFAULT_OWNER}],

      toolsAreVisible: false
    };
    // Create a list of draw modes, including whether they're currently enabled.
    DRAW_MODES.forEach(mode => {
      state.drawModes.push({
        ...mode,
        locks: [], // Services can "lockout" any draw mode, disabling it.
        enabled: true // A draw mode is only enabled when no services have it locked out. TODO: Make computed
      });
    });
    return state;
  };

  remap = {
    LAYERS_LOADED: 'EVALUATE_LAYER_SELECTION'
  };

  getters = {
    ownerI18nKey: () => this.state.locks[this.state.locks.length - 1].displayKey,

    toolsAreLocked: () => this.getters.ownerI18nKey !== DEFAULT_OWNER,

    hasAnnotationLayers ({get}) {
      const annotationsService = get('VIEWPORT').plugins('us:annotations:service');
      return annotationsService.layers.hasReportLayer() && annotationsService.layers.hasWvAnnotationLayer();
    }
  };

  // Listen for uScope annotations events and update state accordingly
  AFTER_VIEWPORT_CREATED ({state, dispatch}, viewport) {
    viewport.events.on('viewport:navigation-change', (mode) => {
      const drawMode = this.state.drawModes.find(m => m.action === mode);
      if (drawMode) {
        this.state.drawMode = drawMode;
        this.state.isDrawing = true;
        dispatch('DRAW_MODE_STARTED');
      }
    });

    viewport.events.on('us:annotations:drawing-complete', (region) => {
      this.state.drawMode = null;
      this.state.isDrawing = false;

      if (region) {
        dispatch('DRAW_MODE_COMPLETE', translateRegion(region));
      }
    });

    viewport.events.on('us:annotations:before-new-region-save', (region) => {
      dispatch('BEFORE_REGION_SAVE', translateRegion(region));
    });

    viewport.events.on('us:annotations:new-region-saved', (region) => {
      dispatch('REGION_SAVED', translateRegion(region));
    });
  }

  START_DRAW_MODE ({get}, mode) {
    const viewport = get('VIEWPORT').currentViewport;
    if (!viewport) {
      return;
    }
    viewport.navModeSwitch(mode.action);
  }

  STOP_DRAW_MODE ({get}) {
    const viewport = get('VIEWPORT').currentViewport;
    viewport && viewport.navModeHalt();
  }

  // Command that allows another service to take control of tools and layer management.
  LOCK_DRAW_MODES ({state}, {lockOwner, displayKey, modes}) {
    const drawModes = state('DRAW_MODE').drawModes;
    modes.forEach(m => {
      const mode = drawModes.find(d => d.name === m);
      // If the requested mode was found, add a lock on it.
      if (mode && mode.locks.includes(lockOwner)) {
        // If this happens it seems like a probable bug.
        logger.warn(`${lockOwner} has already locked ${mode.name}`);
        return;
      }
      mode.locks.push(lockOwner);
      mode.enabled = false; // TODO: This value should be computed (enabled =locks.length = 0)
    });

    pushOnce(this.state.locks, (a) => (b) => a.lockOwner === b.lockOwner, {displayKey: displayKey || lockOwner, lockOwner});
  }

  // Command that allows another service to relinquish layer management control.
  UNLOCK_DRAW_MODES ({state, dispatch}, {lockOwner, modes}) {
    const drawModes = state('DRAW_MODE').drawModes;
    modes.forEach(m => {
      const mode = drawModes.find(d => d.name === m);
      // Check to see if the lock owner has a lock on the mode (assuming the mode is valid)
      let lockIndex = mode ? mode.locks.indexOf(lockOwner) : -1;

      // Remove the lock
      if (lockIndex >= 0) {
        mode.locks.splice(lockIndex, 1);
        mode.enabled = mode.locks.length === 0; // TODO: This value should be computed
      }
    });

    const lock = this.state.locks.find(l => l.lockOwner === lockOwner);
    pull(this.state.locks, lock);

    // Releasing the lock may have reverted control to this store so we need to reevaluate layer selection.
    dispatch('EVALUATE_LAYER_SELECTION');
  }

  // to this method is wrapped in an "if" statement, take that as an indication that the rules have changed and
  // This method encapsulates the business rules around when it's appropriate to create annotations layers. If a call
  // consider whether those conditions are general enough that they should be moved here.
  _isEvaluating = false;
  async EVALUATE_LAYER_SELECTION ({dispatch, get}) {
    // Only act if:
    // 1. we have sync'd layers with uScope
    // 2. the tools are not locked by another service
    // 3. the drawing tools are visible
    if (!this._isEvaluating && get('ANNOTATIONS').currentState.areLayersLoaded && !this.getters.toolsAreLocked && this.state.toolsAreVisible) {
      this._isEvaluating = true;

      if (!this.getters.hasAnnotationLayers) {
        await dispatch('CREATE_ANNOTATIONS_LAYERS');
      }
      await dispatch('SELECT_ANNOTATIONS_LAYER');

      this._isEvaluating = false;
    }
  }

  async CREATE_ANNOTATIONS_LAYERS ({dispatch, get}) {
    const annotationsService = get('VIEWPORT').plugins('us:annotations:service');
    const promises = [];

    try {
      if (!annotationsService.layers.hasReportLayer()) {
        promises.push(
          annotationsService.layers.createAddSaveSpecial(WV_LAYER_NAMES.REPORT)
        );
      }
      if (!annotationsService.layers.hasWvAnnotationLayer()) {
        promises.push(
          annotationsService.layers.createAddSaveSpecial(WV_LAYER_NAMES.ANNOTATION)
        );
      }

      dispatch('LOCK_DRAW_MODES', DRAW_MODE_LOCK);
      await Promise.all(promises);
      dispatch('UNLOCK_DRAW_MODES', DRAW_MODE_LOCK);
    } catch (e) {
      dispatch('UNLOCK_DRAW_MODES', DRAW_MODE_LOCK);
      dispatch('USCOPE_NOTIFY', {type: 'info', message: $t(`failed-image-for-drawing`)});
    }
  }

  SELECT_ANNOTATIONS_LAYER ({get}) {
    const annotationsService = get('VIEWPORT').plugins('us:annotations:service');
    if (annotationsService) {
      annotationsService.layers.selectByProperty('Name', WV_LAYER_NAMES.ANNOTATION);
    }
  }

  async SHOW_DRAWING_TOOLS ({dispatch}) {
    this.state.toolsAreVisible = true;
    await dispatch('EVALUATE_LAYER_SELECTION');
  }

  HIDE_DRAWING_TOOLS ({dispatch}) {
    this.state.toolsAreVisible = false;
    dispatch('STOP_DRAW_MODE');
  }

  TOGGLE_DRAW_TOOLS ({dispatch}) {
    const action = this.state.toolsAreVisible ? 'HIDE_DRAWING_TOOLS' : 'SHOW_DRAWING_TOOLS';
    dispatch(action);
  }
}



// WEBPACK FOOTER //
// ./src/shared/stores/annotations/DrawingStore.js